import { array, boolean, object, string } from 'yup';
import { noSurroundingSpaces } from '@/helpers';

/**
 * Shared by the create and the edit screen, which post the same shape: the full set of addresses
 * and the full set of members.
 */
export const useProductGroupValidations = (t) => {
    const validationSchema = object({
        label: string()
            .required(t('validations.messages.required', { _field_: t('productgroup.detail.label') }))
            .test(noSurroundingSpaces(t('productgroup.detail.label')))
            .max(256, t('validations.messages.maxChar', { _field_: t('productgroup.detail.label'), length: 256 })),
        description: string().nullable(),
        emails: array()
            .of(
                string()
                    .required(t('validations.messages.required', { _field_: t('productgroup.create.email') }))
                    .email(t('productgroup.create.invalidEmail'))
                    .max(
                        320,
                        t('validations.messages.maxChar', { _field_: t('productgroup.create.email'), length: 320 })
                    )
            )
            // The unique index is per group and lives in the database, so a duplicate would come back as a 400.
            .test('unique-emails', t('productgroup.create.duplicateEmail'), (emails) => {
                const addresses = (emails ?? []).map((email) => email?.trim().toLowerCase()).filter((email) => !!email);
                return new Set(addresses).size === addresses.length;
            }),
        productProcessDefinitions: array()
            .of(
                object({
                    productProcessDefinition: object()
                        .nullable()
                        .required(
                            t('validations.messages.required', { _field_: t('productgroup.detail.member.label') })
                        ),
                    switchDependency: boolean()
                })
            )
            .min(2, t('productgroup.create.minimumMembers'))
            // The same definition twice in one group is meaningless; across groups it is allowed.
            .test('unique-definitions', t('productgroup.create.duplicateMember'), (members) => {
                const ids = (members ?? [])
                    .map((member: any) => member?.productProcessDefinition?.id)
                    .filter((id) => id !== undefined && id !== null);
                return new Set(ids).size === ids.length;
            })
    });

    return { validationSchema };
};
